<template lang="pug">
.tableWrap(ref='full')
    slot(name="msg")
    table.customTbl(ref='table' :class='{resizable}' style="width:0")
        thead(ref="thead")
            slot(name="head")

        tbody
            slot(name='body')
</template>
<script setup lang="ts">
import { ref, onMounted, nextTick, useSlots, onBeforeUnmount } from "vue";
let { resizable } = defineProps({
    resizable: Boolean,
});

let thead = ref(null);
let slots = useSlots();

let observer: MutationObserver = null;
let resizers_arr: Element[] = [];
let full = ref(null);
let removeSetup = () => {
    resizers_arr.forEach((resizer) => {
        resizer.removeEventListener("mousedown", mousedown);
    });
    document.removeEventListener("mousemove", mouseMoveHandler);
    document.removeEventListener("mouseup", mouseup);
};

let headFullWidth = 0;
let fakeWidth = ref(0);

// stretchLastColumn writes an inline width straight onto a th, so that width has to
// be handed back before the next measure. Otherwise a column that was last (and got
// stretched to fill the box) keeps the filler width after another column is added
// after it, and the new column lands off-screen past it.
let stretched: { el: HTMLElement; applied: string; prev: string } | null = null;

let releaseStretch = () => {
    if (!stretched) return;
    // Only undo our own value. If it differs the user has dragged the column since,
    // and their width wins.
    if (stretched.el.style.width === stretched.applied) {
        stretched.el.style.width = stretched.prev;
    }
    stretched = null;
};

let stretchLastColumn = () => {
    if (!full.value || !thead.value) return;
    let wrapWidth = full.value.clientWidth;
    let ths = thead.value.querySelectorAll("th");
    if (ths.length < 2) return;

    // measure the columns at their own widths, not at last run's filler width
    releaseStretch();

    // find last non-fixed th
    let lastTh: HTMLElement | null = null;
    for (let i = ths.length - 1; i >= 0; i--) {
        if (!(ths[i] as HTMLElement).classList.contains("fixed")) {
            lastTh = ths[i] as HTMLElement;
            break;
        }
    }
    if (!lastTh) return;

    // measure width of all columns except the stretchable one
    let otherWidth = 0;
    for (let i = 0; i < ths.length; i++) {
        if (ths[i] !== lastTh) {
            otherWidth += (ths[i] as HTMLElement).getBoundingClientRect().width;
        }
    }

    let lastThWidth = lastTh.getBoundingClientRect().width;
    let totalWidth = otherWidth + lastThWidth;

    // only expand last column if all columns together are shorter than parent
    if (totalWidth < wrapWidth) {
        let remaining = wrapWidth - otherWidth - 1;
        if (remaining > 50) {
            let prev = lastTh.style.width;
            lastTh.style.width = `${remaining}px`;
            // record the value as the browser re-serialized it (fractional px get
            // rounded to 6 significant digits), or the release guard never matches
            stretched = { el: lastTh, applied: lastTh.style.width, prev };
        }
    }
};

if (resizable) {
    let lastThCount = 0;

    onMounted(async () => {
        // Check if slot is rendered

        if (slots.head) {
            if (resizable) {
                await setResize();
                lastThCount = thead.value ? thead.value.querySelectorAll("th").length : 0;

                // Create a MutationObserver to watch for changes in the 'thead' element
                observer = new MutationObserver(() => {
                    // Only re-initialize if the number of th columns actually changed
                    let currentThCount = thead.value ? thead.value.querySelectorAll("th").length : 0;
                    if (currentThCount !== lastThCount) {
                        lastThCount = currentThCount;
                        setResize();
                    }
                });

                // Start observing the 'thead' element for configured mutations
                observer.observe(thead.value, {
                    childList: true,
                    subtree: true,
                });
            }
        }
    });
    onBeforeUnmount(() => {
        removeSetup();
        if (observer) {
            observer.disconnect();
        }
    });
}

// ResizeObserver 인스턴스 생성
const resizeObserver = new ResizeObserver((entries) => {
    for (let entry of entries) {
        // tableWrapWidth 계산
        let tableWrapWidth = entry.contentRect.width;

        // customTblWidth 계산
        let customTbl = entry.target.querySelector(".customTbl");
        if (!customTbl) return; // .customTbl 요소가 없는 경우 처리
        let customTblWidth = customTbl.getBoundingClientRect().width;

        // fakeWidth 계산 및 적용
        if (customTblWidth < tableWrapWidth) {
            let fakeWidth = tableWrapWidth - customTblWidth;
            document.body.style.setProperty("--fakeWidth", `${fakeWidth}px`);
        } else {
            document.body.style.setProperty("--fakeWidth", "0px"); // fakeWidth 초기화
        }

        stretchLastColumn();
    }
});

// full.value 요소를 관찰 대상으로 등록
onMounted(() => {
    if (full.value) {
        resizeObserver.observe(full.value);
    }
});
onBeforeUnmount(() => {
    resizeObserver.disconnect();
});

let currentHeadCol: HTMLElement = null;
let pageXMouseDown = 0;
let pageXMouseMoveDiff = 0;
let currentHeadColWidth = 0;

let thTotal = 0;
let currentSiblingHeadWidth = 0;

let mousedown = (e: MouseEvent) => {
    let el = thead.value;
    let ths = el.querySelectorAll("th");
    let th = (e.currentTarget as HTMLElement).parentElement;

    for (let i = 0; i < ths.length; i++) {
        if (ths[i] === th) {
            ths[i].classList.add("hovered");
        } else {
            ths[i].classList.add("nohover");
        }
    }

    thTotal = parseFloat(window.getComputedStyle(el).width);

    pageXMouseDown = e.pageX;
    let currentTarget = e.currentTarget as HTMLElement;
    currentHeadCol = currentTarget.parentNode as HTMLElement;
    if (!currentHeadCol) {
        return;
    }
    currentHeadColWidth = parseFloat(
        window.getComputedStyle(currentHeadCol).width
    );
    if (isNaN(currentHeadColWidth)) {
        return;
    }
    if (currentHeadCol.nextElementSibling) {
        currentSiblingHeadWidth = parseFloat(
            window.getComputedStyle(
                currentHeadCol.nextElementSibling as HTMLElement
            ).width
        );
    }
};

let mouseup = () => {
    currentHeadCol = null;
    pageXMouseDown = pageXMouseMoveDiff;
    if (thead.value) {
        let th = thead.value.querySelectorAll("th");
        for (let i = 0; i < th.length; i++) {
            th[i].classList.remove("hovered");
            th[i].classList.remove("nohover");
        }
    }
};

let mouseMoveHandler = (e) => {
    if (!currentHeadCol) {
        return;
    }

    if (currentHeadCol.classList.contains("fixed")) return;

    // 마우스 이동 거리 계산
    pageXMouseMoveDiff = e.pageX - pageXMouseDown;

    // 새로운 너비 계산
    let newWidth = currentHeadColWidth + pageXMouseMoveDiff;

    // 너비가 음수가 되지 않도록 제한
    if (newWidth < 50) {
        newWidth = 50;
    }

    // 현재 열의 너비만 적용
    currentHeadCol.style.width = `${newWidth}px`;

    stretchLastColumn();

    // .tableWrap 의 너비 가져오기
    if (!full.value) return;
    let tableWrapWidth = parseFloat(window.getComputedStyle(full.value).width);
    // customTbl 의 너비 가져오기
    let customTbl = full.value.querySelector(".customTbl");
    if (!customTbl) return;
    let customTblWidth = parseFloat(
        window.getComputedStyle(customTbl).width
    );
    // customTbl 의 너비가 .tableWrap 의 너비보다 작으면
    if (customTblWidth < tableWrapWidth) {
        // 차이 값 만큼 fakeWidth 값 설정
        fakeWidth.value = tableWrapWidth - customTblWidth;
        // customTblWidth 의 가상요소 너비를 fakeWidth 값으로 설정
        document.body.style.setProperty("--fakeWidth", `${fakeWidth.value}px`);
    }
};

let setResize = async () => {
    // - initiallize start
    removeSetup();
    await nextTick();

    if (!full.value) return;
    headFullWidth = parseFloat(window.getComputedStyle(full.value).width);

    // let tableWrapWidth = parseFloat(window.getComputedStyle(full.value).width);
    // let customTblWidth = parseFloat(window.getComputedStyle(full.value.querySelector('.customTbl')).width);
    // if (customTblWidth < tableWrapWidth) {
    // 	fakeWidth.value = tableWrapWidth - customTblWidth;
    // 	document.body.style.setProperty('--fakeWidth', `${fakeWidth.value + 10}px`);
    // }

    let el = thead.value;
    let th = el.querySelectorAll("th");

    // for (let i = 0; i < th.length - 1; i++) {
    // 	if (th[i].classList.contains('fixed')) continue;
    // 	let style = window.getComputedStyle(th[i]);
    // 	th[i].style.width = style.width;
    // }

    resizers_arr = [];
    // - initiallize end

    let resizers = el.querySelectorAll("th > .resizer:not(.fixed)");
    for (let i = 0; i < resizers.length; i++) {
        (resizers[i] as HTMLElement).addEventListener("mousedown", mousedown);
        resizers_arr.push(resizers[i]);
    }
    document.addEventListener("mousemove", mouseMoveHandler);
    document.addEventListener("mouseup", mouseup);

    stretchLastColumn();
};
</script>

<style lang="less">
@import "@/assets/less/bq-tokens.less";

.customTbl.resizable>thead>tr>th>.resizer {
    cursor: col-resize;
}

.table-cont-wrap {
    position: relative;
}

.tableWrap {
    position: relative;
    overflow-x: auto;
    overflow-y: hidden;
    box-sizing: border-box;
}

.tableMsg {
    position: absolute;
    top: 58px;
    left: 0;
    color: @muted;
    font-size: 0.875rem;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    z-index: 2;

    &.left {
        left: 16px;
    }

    &.center {
        left: 50%;
        transform: translateX(-50%);
    }

    &.empty {
        &~.customTbl {
            tbody {
                tr {
                    pointer-events: none;

                    &:hover {
                        background: transparent;
                    }
                }
            }
        }
    }
}

.customTbl {
    width: 100%;
    min-width: fit-content;
    border-collapse: collapse;
    table-layout: fixed;
    font-family: @mono;
    color: @ink;
    font-size: 0.85rem;

    &.resizable {
        thead {
            th {
                &.hovered,
                &:hover {
                    .resizer {
                        display: block;
                    }
                }
            }
        }
    }

    .overflow {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .left {
        text-align: left;
    }

    thead {
        background: @paper;
        text-align: left;
        border-bottom: 1px solid @line;

        tr {
            position: relative;
            height: 2.625rem;
        }

        th {
            position: relative;
            font-size: 0.78rem;
            font-weight: 600;
            color: @muted;
            padding: 0.65rem 1rem;
            white-space: nowrap;
            user-select: none;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            text-overflow: ellipsis;

            span {
                display: inline-block;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                max-width: 100%;
            }

            &.center {
                text-align: center;
            }

            &.nohover,
            &.fixed {
                &:hover {
                    .resizer {
                        display: none;
                    }
                }
            }

            &:hover {
                background: @hover-bg;
            }

            .resizer {
                position: absolute;
                top: 0;
                right: -6px;
                width: 12px;
                border-left: 4px solid transparent;
                border-right: 4px solid transparent;
                display: none;
                height: 100%;

                &::before {
                    content: "";
                    display: block;
                    height: 100%;
                    width: 1px;
                    background-color: @line;
                }
            }
        }
    }

    tbody {
        background: @paper;

        tr {
            position: relative;
            height: 3rem;
            border-bottom: 1px solid @hover-bg;
            transition: background 0.1s;

            &:hover {
                cursor: pointer;
                background: @hover-bg;
            }

            &:last-child {
                border-bottom: none;
            }
        }

        td {
            position: relative;
            padding: 0.7rem 1rem;

            &.center {
                text-align: center;
            }
        }
    }

    .nohover {
        &:hover {
            cursor: default !important;
            background: transparent !important;
        }
    }

    .empty-value {
        position: absolute;
        top: 78px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 1;
        pointer-events: none;
        cursor: default;
        height: fit-content;

        ~tr {
            &:hover {
                cursor: default;
                background: transparent;

                td {
                    border: none;
                }
            }
        }

        &:hover {
            cursor: default;
            background: transparent;

            ~tr {
                pointer-events: none;

                &:hover {
                    background: transparent;
                }
            }
        }
    }
}
</style>
